import { useState } from 'react';
import { Button, Checkbox, Popover } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { TelemetryVital } from '@/apis/telemetry';
import {
  CHART_MEASURES,
  chartsTriggerLabel,
  DEFAULT_CHARTS,
  isLastPicked,
  shownCharts,
  toggleChart,
} from '../lib/measures';
import styles from './ChartPicker.module.css';

/** Props for {@link ChartPicker}. */
export interface ChartPickerProps {
  available: readonly TelemetryVital[];
  shown: readonly TelemetryVital[];
  onApply: (charts: TelemetryVital[]) => void;
  fullWidth: boolean;
}

/**
 * The "N Charts" dropdown: tick the charts to show, then Apply; at least one stays ticked. Styled to Figma: black
 * checkboxes, a text Reset and an inverse-filled Apply. `fullWidth` stretches the trigger and the list across the row
 * (phones).
 */
const ChartPicker = ({
  available,
  shown,
  onApply,
  fullWidth,
}: ChartPickerProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<readonly TelemetryVital[]>(shown);

  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(shown);
    setOpen(next);
  };

  const toggle = (key: TelemetryVital, checked: boolean) =>
    setDraft((prev) => toggleChart(prev, key, checked, available));

  const apply = () => {
    onApply([...draft]);
    setOpen(false);
  };

  const content = (
    <div className={fullWidth ? styles.menuFull : styles.menu}>
      <div className={styles.heading} id='sensor-chart-picker-heading'>
        Available charts
      </div>
      <div role='group' aria-labelledby='sensor-chart-picker-heading'>
        {available.map((key) => {
          const checked = draft.includes(key);
          return (
            <Checkbox
              key={key}
              className={styles.option}
              checked={checked}
              disabled={isLastPicked(draft, key)}
              onChange={(event) => toggle(key, event.target.checked)}
            >
              {CHART_MEASURES[key].label}
            </Checkbox>
          );
        })}
      </div>
      <div className={styles.actions}>
        <Button
          type='text'
          className={styles.action}
          onClick={() => setDraft(shownCharts(DEFAULT_CHARTS, available))}
        >
          Reset
        </Button>
        <Button
          color='default'
          variant='solid'
          className={styles.action}
          onClick={apply}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger='click'
      placement={fullWidth ? 'bottom' : 'bottomRight'}
      getPopupContainer={
        fullWidth
          ? (trigger) => trigger.parentElement ?? document.body
          : undefined
      }
      classNames={{
        root: fullWidth ? styles.popoverFull : undefined,
        container: styles.panel,
      }}
      arrow={false}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <button
        type='button'
        className={fullWidth ? styles.triggerFull : styles.trigger}
        aria-haspopup='true'
        aria-expanded={open}
      >
        {chartsTriggerLabel(shown, available, fullWidth)}
        <DownOutlined className={styles.chevron} />
      </button>
    </Popover>
  );
};

export default ChartPicker;
